import { queueFiles, dropQueued, pendingFor } from './uploadQueue';

const API_BASE = process.env.REACT_APP_API_URL || '';
const DEMO_MODE = process.env.REACT_APP_DEMO_MODE === 'true';

// Identificator de dispozitiv pentru rate limiting.
// Adresa de retea nu e utilizabila ca cheie: pe 4G/5G operatorii folosesc CGNAT,
// deci invitati de la evenimente diferite ajung pe acelasi IP si s-ar bloca
// reciproc, in timp ce pe IPv6 fiecare telefon isi poate roti adresa.
const DEVICE_KEY = 'qr_device_id';

function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`)
        .replace(/[^a-zA-Z0-9_-]/g, '');
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    // Mod incognito / storage blocat: cadem pe limitarea per IP de pe server.
    return '';
  }
}

// Serverul poate fi o versiune fara upload pe bucati (deploy in curs). Aflam o
// singura data, la primul init respins, si nu mai insistam.
let chunkedAvailable = true;

// Ecranul care se stinge omoara upload-ul: telefonul pus pe masa in timp ce urca
// un clip era cea mai frecventa cauza de upload neterminat. Il tinem aprins cat
// dureaza, ca la aplicatiile de navigatie. Unde nu exista API-ul, mergem mai
// departe fara — nu e o functie de care sa depinda upload-ul.
function createWakeLock() {
  let lock = null;
  let active = false;

  const request = async () => {
    if (!active || document.visibilityState !== 'visible') return;
    try { lock = await navigator.wakeLock.request('screen'); } catch {}
  };
  // Sistemul elibereaza lock-ul cand pagina trece in fundal; il recastigam la revenire.
  const onVisible = () => { if (document.visibilityState === 'visible') request(); };

  return {
    async start() {
      if (!navigator.wakeLock) return;
      active = true;
      document.addEventListener('visibilitychange', onVisible);
      await request();
    },
    stop() {
      active = false;
      document.removeEventListener('visibilitychange', onVisible);
      try { if (lock) lock.release(); } catch {}
      lock = null;
    }
  };
}

// ===== TRIMITERE IN FUNDAL (Background Fetch) =====
//
// Singurul mod prin care upload-ul continua dupa ce invitatul iese din browser
// si nu mai revine. Exista doar in Chrome pe Android; pe iPhone nu are
// echivalent, acolo ne bazam pe coada care se reia la urmatoarea deschidere.
let swRegistration;

// Cat asteptam un raspuns de la Background Fetch inainte sa ne descurcam singuri.
const BG_FETCH_TIMEOUT = 8000;
const TIMED_OUT = Symbol('timeout');

// Promisiune care nu se mai intoarce niciodata = invitat blocat cu ecranul mort.
// Nu o anulam (nu se poate), doar nu o mai asteptam.
function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(TIMED_OUT), ms)),
  ]);
}

function getServiceWorker() {
  if (swRegistration === undefined) {
    swRegistration = ('serviceWorker' in navigator)
      ? navigator.serviceWorker.register('/sw.js').catch(() => null)
      : Promise.resolve(null);
  }
  return swRegistration;
}

// ===== DEMO MOCK DATA =====
const DEMO_EVENT = {
  id: 'demo-event-id',
  slug: 'demo',
  name: 'Nunta Cristina & Ștefan',
  date: '2025-06-15',
  couple_names: 'Cristina & Ștefan',
  event_type: 'wedding',
  description: 'Împarte-ne cele mai frumoase momente surprinse de tine! Încarcă pozele și clipurile tale din această zi specială.',
  cover_color: '#1a1a2e',
  accent_color: '#c9a96e',
  is_active: 1,
  public_gallery: 1,
  upload_count: 5,
  welcome_title: null,
  welcome_audio: null,
  auto_close_days: null,
};

const DEMO_GALLERY = {
  files: [
    { id: '1', url: '/demo/poza1.jpg', thumbnail_url: '/demo/poza1.jpg', file_type: 'image', uploader_name: 'Maria', message: '', uploaded_at: '2025-06-15T20:00:00' },
    { id: '2', url: '/demo/poza2.jpg', thumbnail_url: '/demo/poza2.jpg', file_type: 'image', uploader_name: 'Alexandru', message: '', uploaded_at: '2025-06-15T20:05:00' },
    { id: '3', url: '/demo/poza3.jpg', thumbnail_url: '/demo/poza3.jpg', file_type: 'image', uploader_name: 'Elena', message: '', uploaded_at: '2025-06-15T20:10:00' },
    { id: '4', url: '/demo/poza4.jpg', thumbnail_url: '/demo/poza4.jpg', file_type: 'image', uploader_name: 'Andrei', message: '', uploaded_at: '2025-06-15T20:15:00' },
    { id: '5', url: '/demo/poza5.jpg', thumbnail_url: '/demo/poza5.jpg', file_type: 'image', uploader_name: 'Cristina', message: '', uploaded_at: '2025-06-15T20:20:00' },
  ],
  total: 5,
  page: 1,
  pages: 1,
};

export const api = {
  async fetch(endpoint, options = {}) {
    const res = await fetch(`${API_BASE}${endpoint}`, {
      headers: { 'Content-Type': 'application/json', 'X-Device-Id': getDeviceId(), ...options.headers },
      ...options,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(err.error || 'Request failed');
    }
    return res.json();
  },

  createEvent: (data) => api.fetch('/api/events', { method: 'POST', body: JSON.stringify(data) }),
  getEvent: (slug) => {
    if (DEMO_MODE) return Promise.resolve(DEMO_EVENT);
    return api.fetch(`/api/events/${slug}`);
  },
  updateEvent: (slug, data) => api.fetch(`/api/events/${slug}`, { method: 'PUT', body: JSON.stringify(data) }),
  archiveEvent: (slug, admin_password) => api.fetch(`/api/events/${slug}/archive`, { method: 'POST', body: JSON.stringify({ admin_password }) }),
  deleteEvent: (slug, admin_password) => api.fetch(`/api/events/${slug}`, { method: 'DELETE', body: JSON.stringify({ admin_password }) }),
  authEvent: (slug, admin_password) => api.fetch(`/api/events/${slug}/auth`, { method: 'POST', body: JSON.stringify({ admin_password }) }),
  getAdminEvents: (admin_password) => api.fetch('/api/admin/events', { method: 'POST', body: JSON.stringify({ admin_password }) }),

  getGallery: (slug, page = 1) => {
    if (DEMO_MODE) return Promise.resolve(DEMO_GALLERY);
    return api.fetch(`/api/events/${slug}/gallery?page=${page}&limit=50`);
  },

  // Aduce TOATE pozele, nu doar prima pagina. Fara asta, galeriile cu peste 50
  // de fisiere se afisau trunchiate — invitatii nu vedeau decat inceputul.
  async getAllGallery(slug) {
    if (DEMO_MODE) return DEMO_GALLERY;
    const first = await api.fetch(`/api/events/${slug}/gallery?page=1&limit=100`);
    let files = first.files || [];
    const pages = first.pages || 1;
    for (let p = 2; p <= pages; p++) {
      const next = await api.fetch(`/api/events/${slug}/gallery?page=${p}&limit=100`);
      files = files.concat(next.files || []);
    }
    return { ...first, files };
  },
  deleteUpload: (uploadId, admin_password) => api.fetch(`/api/uploads/${uploadId}`, { method: 'DELETE', body: JSON.stringify({ admin_password }) }),

  getSlideshow: (slug, since = '') => api.fetch(`/api/events/${slug}/slideshow${since ? `?since=${encodeURIComponent(since)}` : ''}`),

  getGuestbook: (slug) => {
    if (DEMO_MODE) return Promise.resolve({ welcome_audio_url: null, welcome_title: null, messages: [] });
    return api.fetch(`/api/events/${slug}/guestbook`);
  },
  deleteWelcomeAudio: (slug, admin_password) => api.fetch(`/api/events/${slug}/welcome-audio`, { method: 'DELETE', body: JSON.stringify({ admin_password }) }),

  async uploadWelcomeAudio(slug, file, adminPassword) {
    const formData = new FormData();
    formData.append('audio', file);
    formData.append('admin_password', adminPassword);
    const res = await fetch(`${API_BASE}/api/events/${slug}/welcome-audio`, { method: 'POST', body: formData });
    if (!res.ok) throw new Error('Upload failed');
    return res.json();
  },

  async uploadAudio(slug, audioBlob, uploaderName) {
    if (DEMO_MODE) throw new Error('DEMO_MODE');
    const formData = new FormData();
    formData.append('audio', audioBlob, 'voice_message.webm');
    if (uploaderName) formData.append('uploader_name', uploaderName);
    const res = await fetch(`${API_BASE}/api/events/${slug}/guestbook`, { method: 'POST', body: formData });
    if (!res.ok) throw new Error('Upload failed');
    return res.json();
  },

  // ===== UPLOAD PE BUCATI =====
  //
  // Trimiterea fisierului intreg intr-un singur request nu supravietuieste pe
  // mobil: la o intrerupere se pierde tot progresul si totul reia de la zero. Pe
  // felii, serverul stie exact cati octeti are, iar la revenire continuam de
  // acolo — chiar daca invitatul a inchis pagina intre timp.

  // Acelasi fisier ales din nou trebuie recunoscut, altfel reluarea n-are sens.
  sessionKey(slug, file) {
    return `qr_up_${slug}_${file.size}_${file.lastModified}_${file.name}`;
  },

  loadSession(key) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const s = JSON.parse(raw);
      // Serverul tine sesiunile 24h; peste asta nu mai are rost sa intrebam.
      if (!s.sessionId || Date.now() - (s.at || 0) > 24 * 60 * 60 * 1000) {
        localStorage.removeItem(key);
        return null;
      }
      return s;
    } catch { return null; }
  },

  saveSession(key, data) {
    try { localStorage.setItem(key, JSON.stringify({ ...data, at: Date.now() })); } catch {}
  },

  clearSession(key) {
    try { localStorage.removeItem(key); } catch {}
  },

  // Sterge referintele ramase de la upload-uri vechi, ca sa nu creasca la infinit.
  pruneSessions() {
    try {
      const stale = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k || !k.startsWith('qr_up_')) continue;
        try {
          const s = JSON.parse(localStorage.getItem(k));
          if (!s || Date.now() - (s.at || 0) > 24 * 60 * 60 * 1000) stale.push(k);
        } catch { stale.push(k); }
      }
      stale.forEach(k => localStorage.removeItem(k));
    } catch {}
  },

  async initSession(slug, file, uploaderName, message, clientToken) {
    const res = await fetch(`${API_BASE}/api/events/${slug}/upload/init`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Device-Id': getDeviceId() },
      body: JSON.stringify({
        filename: file.name, size: file.size, mime: file.type || '',
        uploader_name: uploaderName || '', message: message || '',
        // Marca fisierului: daca a ajuns deja, serverul ne spune si nu-l dublam.
        client_token: clientToken || ''
      }),
    });

    let body = null;
    try { body = await res.json(); } catch {}

    if (!res.ok) {
      // Raspuns fara JSON inseamna ca serverul nu cunoaste ruta (versiune veche):
      // nu e o eroare de upload, ci semnalul sa folosim calea clasica.
      if (!body) { const e = new Error('chunked-indisponibil'); e.fallback = true; throw e; }
      const err = new Error(body.error || 'Nu am putut porni încărcarea.');
      err.permanent = (res.status >= 400 && res.status < 500) || res.status === 507;
      throw err;
    }
    return body;
  },

  async sessionStatus(slug, sessionId) {
    try {
      const res = await fetch(`${API_BASE}/api/events/${slug}/upload/${sessionId}/status`, {
        headers: { 'X-Device-Id': getDeviceId() },
      });
      if (!res.ok) return null;
      return await res.json();
    } catch { return null; }
  },

  putChunk(slug, sessionId, offset, blob, onBytes) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', `${API_BASE}/api/events/${slug}/upload/${sessionId}/chunk`);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      xhr.setRequestHeader('X-Offset', String(offset));
      xhr.setRequestHeader('X-Device-Id', getDeviceId());

      // Progresul curge in interiorul feliei, altfel bara ar sta pe loc
      // zeci de secunde pe net slab.
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onBytes) onBytes(offset + e.loaded);
      });

      xhr.onload = () => {
        let body = null;
        try { body = JSON.parse(xhr.responseText); } catch {}
        if (xhr.status >= 200 && xhr.status < 300) return resolve(body || {});
        // Serverul e in alt punct decat credeam: ne resincronizam si continuam.
        if (xhr.status === 409 && body && typeof body.received === 'number') {
          return resolve({ received: body.received, resync: true });
        }
        const err = new Error((body && body.error) || 'Felie respinsă');
        err.permanent = xhr.status === 413 || xhr.status === 415 || xhr.status === 507;
        err.gone = xhr.status === 404;
        reject(err);
      };
      xhr.onerror = () => reject(new Error('network'));
      xhr.ontimeout = () => reject(new Error('timeout'));
      xhr.send(blob);
    });
  },

  async finishSession(slug, sessionId, uploaderName, message, clientToken) {
    const res = await fetch(`${API_BASE}/api/events/${slug}/upload/${sessionId}/finish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Device-Id': getDeviceId() },
      body: JSON.stringify({
        uploader_name: uploaderName || '', message: message || '',
        client_token: clientToken || ''
      }),
    });
    let body = null;
    try { body = await res.json(); } catch {}
    if (!res.ok) {
      const err = new Error((body && body.error) || 'Finalizare eșuată');
      err.incomplete = res.status === 409;
      err.gone = res.status === 404;
      err.permanent = res.status === 403;
      throw err;
    }
    return body;
  },

  // Urca un fisier pe felii, reluand din ultimul punct confirmat de server.
  async uploadResumable(slug, file, uploaderName, message, onBytes) {
    const key = api.sessionKey(slug, file);
    let saved = api.loadSession(key);
    let sessionId = saved && saved.sessionId;
    let chunkSize = (saved && saved.chunkSize) || 5 * 1024 * 1024;
    let offset = 0;

    if (sessionId) {
      const st = await api.sessionStatus(slug, sessionId);
      // Sesiunea a expirat sau serverul a fost curatat: pornim una noua.
      if (st && st.size === file.size) {
        offset = st.received;
        chunkSize = st.chunkSize || chunkSize;
      } else {
        api.clearSession(key);
        sessionId = null;
      }
    }

    if (!sessionId) {
      const init = await api.initSession(slug, file, uploaderName, message, key);
      // Fisierul era deja urcat (reluare dupa un raspuns pierdut): gata.
      if (init.duplicate) { api.clearSession(key); return init; }
      sessionId = init.sessionId;
      chunkSize = init.chunkSize || chunkSize;
      offset = init.received || 0;
      api.saveSession(key, { sessionId, chunkSize, size: file.size });
    }

    while (offset < file.size) {
      const end = Math.min(offset + chunkSize, file.size);
      let res;
      try {
        res = await api.putChunk(slug, sessionId, offset, file.slice(offset, end), onBytes);
      } catch (err) {
        // Sesiunea a disparut de pe server: o luam de la capat, nu abandonam.
        if (err.gone) { api.clearSession(key); throw Object.assign(err, { retryFresh: true }); }
        throw err;
      }
      offset = res.received;
      api.saveSession(key, { sessionId, chunkSize, size: file.size, offset });
      if (onBytes) onBytes(offset);
    }

    const done = await api.finishSession(slug, sessionId, uploaderName, message, key);
    api.clearSession(key);
    return done;
  },

  // Trimite UN singur fisier. Bucata elementara a upload-ului rezilient.
  uploadOne(slug, file, uploaderName, message, onBytes) {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('files', file);
      if (uploaderName) formData.append('uploader_name', uploaderName);
      if (message) formData.append('message', message);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE}/api/events/${slug}/upload`);
      xhr.setRequestHeader('X-Device-Id', getDeviceId());

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onBytes) onBytes(e.loaded);
      });
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          // Pastreaza mesajul explicit al serverului (ex. fisier prea mare),
          // altfel invitatul vede o eroare generica si reincearca degeaba.
          let msg = 'Upload failed';
          // Nu reincercam ce nu se poate schimba in cateva secunde: fisier prea
          // mare, eveniment inchis (4xx), rate limit (fereastra e de 10 minute,
          // deci reincercarea doar consuma din buget) sau disc plin (507).
          let permanent = (xhr.status >= 400 && xhr.status < 500) || xhr.status === 507;
          try {
            const body = JSON.parse(xhr.responseText);
            if (body && body.error) msg = body.error;
          } catch {}
          const err = new Error(msg);
          err.permanent = permanent;
          reject(err);
        }
      };
      xhr.onerror = () => reject(new Error('network'));
      xhr.ontimeout = () => reject(new Error('timeout'));
      xhr.send(formData);
    });
  },

  // Upload secvential, fisier cu fisier, cu reincercari.
  //
  // Pe mobil conexiunea pica des: handoff intre celule, trecere WiFi<->4G,
  // semnal pierdut. Cu tot lotul intr-un singur request, o pica la 90% insemna
  // pierderea a tot. Asa, fiecare fisier reusit ramane salvat, iar cel in curs
  // se reia din ultima felie confirmata de server.
  async uploadFiles(slug, files, uploaderName, message, onProgress, opts = {}) {
    if (DEMO_MODE) throw new Error('DEMO_MODE');

    api.pruneSessions();
    const wakeLock = createWakeLock();
    await wakeLock.start();

    // Cele mici intai: daca invitatul pleaca la jumatatea treabii, pozele sunt
    // deja salvate si ramane in aer doar clipul lung.
    const queue = [...files].sort((a, b) => a.size - b.size);

    // Fisierele trec prin IndexedDB inainte sa plece: daca invitatul iese acum
    // si revine peste o ora, le gasim aici si terminam treaba fara sa-i cerem
    // sa le aleaga din nou. Cele deja in coada (o reluare) nu se rescriu.
    if (opts.persist !== false) {
      await queueFiles(queue.map(f => ({
        id: api.sessionKey(slug, f), slug, file: f, uploaderName, message
      })));
    }

    const totalBytes = queue.reduce((sum, f) => sum + f.size, 0) || 1;
    let completedBytes = 0;
    const uploaded = [];
    const failed = [];

    try {
      for (let i = 0; i < queue.length; i++) {
        const file = queue[i];
        let lastError = null;

        // Progresul e ponderat pe octeti, nu pe numar de fisiere: altfel un clip
        // de 200 MB ar avansa bara la fel de mult ca o poza de 1 MB.
        const report = (bytesThisFile) => {
          if (!onProgress) return;
          const pct = Math.min(99, Math.round(((completedBytes + bytesThisFile) / totalBytes) * 100));
          onProgress(pct, { index: i, total: queue.length, name: file.name });
        };

        for (let attempt = 0; attempt < 4; attempt++) {
          try {
            const res = chunkedAvailable
              ? await api.uploadResumable(slug, file, uploaderName, message, report)
              : await api.uploadOne(slug, file, uploaderName, message, report);
            uploaded.push(...(res.files || []));
            await dropQueued(api.sessionKey(slug, file));
            lastError = null;
            break;
          } catch (err) {
            // Server fara upload pe bucati: trecem definitiv pe calea clasica.
            if (err.fallback) {
              chunkedAvailable = false;
              attempt--;
              continue;
            }
            lastError = err;
            // Fisier prea mare, eveniment inchis, format respins: reincercarea
            // ar da acelasi rezultat. Il scoatem si din coada, altfel l-am
            // reincerca la fiecare deschidere a paginii.
            if (err.permanent) { await dropQueued(api.sessionKey(slug, file)); break; }
            if (attempt < 3) {
              await new Promise(r => setTimeout(r, [1000, 3000, 8000][attempt]));
            }
          }
        }

        completedBytes += file.size;
        if (lastError) failed.push({ name: file.name, error: lastError.message });
        report(0);
      }
    } finally {
      wakeLock.stop();
    }

    if (onProgress) onProgress(100, { index: queue.length, total: queue.length, name: '' });

    // Esec total: aruncam, ca sa vada mesajul serverului.
    if (uploaded.length === 0 && failed.length > 0) {
      throw new Error(failed[0].error);
    }
    return { uploaded: uploaded.length, files: uploaded, failed };
  },

  // Preda fisierele sistemului de operare, ca sa le duca la capat chiar daca
  // invitatul inchide browserul. Intoarce true doar daca a fost acceptat —
  // altfel apelantul trimite normal, din pagina.
  //
  // Aici fiecare fisier pleaca intr-un singur request (asa lucreaza Background
  // Fetch), deci nu avem reluare din mijloc; in schimb nu depinde de pagina
  // deschisa. Marca din X-Queue-Id ii spune service worker-ului ce sa scoata
  // din coada, iar client_token impiedica dublarea daca ceva se reia.
  async uploadInBackground(slug, files, uploaderName, message) {
    if (DEMO_MODE) return false;
    const reg = await getServiceWorker();
    if (!reg || !reg.backgroundFetch) return false;
    // Daca omul a refuzat deja descarcarile automate, nu-l mai intrebam a doua oara.
    try {
      const perm = await navigator.permissions.query({ name: 'background-fetch' });
      if (perm.state === 'denied') return false;
    } catch {}

    const items = files.map(f => ({ id: api.sessionKey(slug, f), file: f }));
    // Plasa de siguranta: daca sistemul renunta, le gasim la urmatoarea vizita.
    await queueFiles(items.map(it => ({ id: it.id, slug, file: it.file, uploaderName, message })));

    const requests = items.map(({ id, file }) => {
      const form = new FormData();
      form.append('files', file, file.name);
      if (uploaderName) form.append('uploader_name', uploaderName);
      if (message) form.append('message', message);
      form.append('client_token', id);
      return new Request(`${API_BASE}/api/events/${slug}/upload`, {
        method: 'POST',
        body: form,
        headers: { 'X-Device-Id': getDeviceId(), 'X-Queue-Id': id },
      });
    });

    try {
      // Chrome poate cere voie pentru descarcari automate, si atunci promisiunea
      // sta nemiscata pana raspunde omul — uneori pentru totdeauna, daca pagina
      // nu mai e in fata. Nu tinem invitatul blocat: dupa cateva secunde trimitem
      // din pagina, iar daca sistemul porneste totusi mai tarziu, client_token
      // face ca a doua sosire sa nu dubleze nimic in galerie.
      const started = await withTimeout(
        reg.backgroundFetch.fetch(`qr|${slug}|${Date.now()}`, requests, {
          title: files.length === 1 ? 'Trimitem fișierul tău...' : `Trimitem ${files.length} fișiere...`,
          icons: [{ src: '/omg-logo.png', sizes: '192x192', type: 'image/png' }],
        }),
        BG_FETCH_TIMEOUT
      );
      return started !== TIMED_OUT;
    } catch {
      // Versiuni de Chrome care nu accepta request-uri cu continut, cota
      // depasita, utilizator care a refuzat: mergem pe calea din pagina.
      return false;
    }
  },

  // Daca sistemul duce chiar acum fisierele, nu pornim acelasi upload din pagina.
  async backgroundUploadRunning() {
    const reg = await getServiceWorker();
    if (!reg || !reg.backgroundFetch) return false;
    try {
      const ids = await withTimeout(reg.backgroundFetch.getIds(), BG_FETCH_TIMEOUT);
      return ids !== TIMED_OUT && ids.length > 0;
    } catch { return false; }
  },

  // Termina ce a ramas in aer de la vizita trecuta.
  //
  // Se cheama la deschiderea paginii evenimentului: daca invitatul a iesit la
  // jumatatea unui clip, nu-i mai cerem sa aleaga fisierele din nou — le avem
  // in coada, iar serverul stie cati octeti a primit deja.
  async resumePending(slug, onProgress, onStart) {
    // Sistemul le duce chiar acum: daca am incepe si noi, s-ar trimite de doua ori.
    if (await api.backgroundUploadRunning()) return null;

    const pending = await pendingFor(slug);
    if (pending.length === 0) return null;
    // Anuntam abia acum: pana aici nu stiam daca e ceva de reluat, iar un
    // mesaj de "continuam" pe ecranul unui invitat nou ar deruta.
    if (onStart) onStart(pending.length);

    // Loturi separate dupa cine trimite: pe acelasi telefon pot ramane fisiere
    // de la doi invitati care si-au dat pe rand telefonul.
    const groups = new Map();
    for (const r of pending) {
      const k = `${r.uploaderName}|${r.message}`;
      if (!groups.has(k)) groups.set(k, { uploaderName: r.uploaderName, message: r.message, files: [] });
      groups.get(k).files.push(r.file);
    }

    let uploaded = 0;
    const failed = [];
    for (const g of groups.values()) {
      try {
        const res = await api.uploadFiles(slug, g.files, g.uploaderName, g.message, onProgress, { persist: false });
        uploaded += res.uploaded;
        failed.push(...(res.failed || []));
      } catch (err) {
        failed.push(...g.files.map(f => ({ name: f.name, error: err.message })));
      }
    }
    return { uploaded, failed, total: pending.length };
  },

  getQRCodeUrl: (slug, format = 'png') => `${API_BASE}/api/events/${slug}/qrcode?format=${format}`,

  async downloadAll(slug, admin_password) {
    const res = await fetch(`${API_BASE}/api/events/${slug}/download`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ admin_password }),
    });
    if (!res.ok) throw new Error('Download failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug}_photos.zip`;
    a.click();
    URL.revokeObjectURL(url);
  },

  getFileUrl: (path) => `${API_BASE}${path}`,
};

export default api;
